import { useEffect, useState } from 'react'

interface ValidationState {
  touched: boolean
  error: string
}

interface FormValidationState {
  firstName: ValidationState
  lastName: ValidationState
}

const useAddContactFormValidation = (
  firstName: string,
  lastName: string,
  isOrganization: boolean,
): {
  validationState: FormValidationState
  isBasicInfoValid: boolean
  handleBlur: (f: keyof FormValidationState) => void
  shouldShowError: (f: keyof FormValidationState) => boolean
  getErrorMessage: (f: keyof FormValidationState) => string
  resetValidation: () => void
} => {
  const [validationState, setValidationState] = useState<FormValidationState>({
    firstName: { touched: false, error: '' },
    lastName: { touched: false, error: '' },
  })

  const [isBasicInfoValid, setIsBasicInfoValid] = useState(false)

  const validateField = (field: keyof FormValidationState): string => {
    switch (field) {
      case 'firstName':
        return !firstName.trim() ? 'First name is required' : ''
      case 'lastName':
        return !isOrganization && !lastName.trim() ? 'Last name is required' : ''
      default:
        return ''
    }
  }

  useEffect(() => {
    const newValidationState = {
      firstName: {
        ...validationState.firstName,
        error: validateField('firstName'),
      },
      lastName: {
        ...validationState.lastName,
        error: validateField('lastName'),
      },
    }

    setValidationState(newValidationState)

    const hasErrors =
      newValidationState.firstName.error !== '' || newValidationState.lastName.error !== ''
    setIsBasicInfoValid(!hasErrors)
  }, [
    firstName,
    lastName,
    validationState.firstName.touched,
    validationState.lastName.touched,
    isOrganization,
  ])

  const handleBlur = (field: keyof FormValidationState): void => {
    setValidationState((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        touched: true,
      },
    }))
  }

  const shouldShowError = (field: keyof FormValidationState): boolean => {
    return validationState[field].touched && validationState[field].error !== ''
  }

  const getErrorMessage = (field: keyof FormValidationState): string => {
    if (shouldShowError(field)) {
      return validationState[field].error
    }
    return ''
  }

  const resetValidation = (): void => {
    setValidationState({
      firstName: { touched: false, error: '' },
      lastName: { touched: false, error: '' },
    })
  }

  return {
    validationState,
    isBasicInfoValid,
    handleBlur,
    shouldShowError,
    getErrorMessage,
    resetValidation,
  }
}

export default useAddContactFormValidation
